import Head from 'next/head';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';

import {
  Box,
  Button,
  DefaultTheme,
  FormatNumber,
  Heading,
  InformationGrid,
  JsonFields,
  LoadingIndicator,
  Spacing,
  Text,
  TimeTable,
  addDays,
  convertToString,
  convertToTimeString,
  formatTime,
  getEndOfWeek,
  getQueryString,
  getStartOfWeek,
  makeStyles
} from 'groundkeeper-component-library';

import { fetchBookingPlans } from 'shared/Redux/slices/bookingPlan.slice';
import { fetchPortalGroupWithUnits } from 'shared/Redux/slices/group.slice';
import { fetchProvider } from 'shared/Redux/slices/provider.slice';
import { fetchPublicBooking } from 'shared/Redux/slices/booking.slice';

import selectBookingById from 'shared/Redux/selectors/booking/selectBookingById';
import selectDateSeriesById from 'shared/Redux/selectors/dateSeries/selectDateSeriesById';
import selectGroupById from 'shared/Redux/selectors/group/selectGroupById';
import selectDefaultOrganization from 'shared/Redux/selectors/organization/selectDefaultOrganization';
import selectProviderById from 'shared/Redux/selectors/provider/selectProviderById';

import classNames from 'shared/SharedJS/src/utils/react/classNames';
import formatDefaultAddressDetail from 'shared/Redux/selectors/address/formatDefaultAddressDetail';
import formatUrl from 'shared/App/utils/formatUrl';
import getDefaultAddress from 'shared/Redux/selectors/address/getDefaultAddress';
import jsonStrings from 'shared/SharedJS/src/utils/intl/jsonStrings';
import useFilter from 'shared/SharedJS/src/utils/hooks/useFilter';
import { shortenText } from 'shared/SharedJS/src/components/TextShortener';
import getDateSeriesLimits from 'shared/SharedJS/src/utils/date/getDateSeriesLimits';
import {
  Group as GroupInterface,
  Holiday,
  Occasion,
  Season
} from 'shared/Redux/interfaces';
import { AppDispatch } from 'shared/Redux/slices';
import { Weekdays } from 'shared/Redux/api/enums';
import { wrapper } from 'shared/Redux/store';

import AuthenticationArea from 'shared/App/components/AuthenticationArea';
import Breadcrumb from 'shared/App/components/Breadcrumb';
import ClusterDrawer from 'shared/App/drawers/ClusterDrawer';
import Drawer from 'shared/App/components/Drawer';
import Layout from 'shared/App/components/Layout';
import Link from 'shared/App/components/Link';
import LinkList from 'shared/App/components/LinkList';
import PageNotFound from 'pages/404';
import TimeTableFilterForm, {
  TimeTableFilterFormValues
} from 'shared/App/forms/TimeTableFilterForm';
import DateTable from 'shared/App/tables/DateTable';
import Globals from 'shared/App/utils/Globals';

interface GroupFilter {
  day_from: Date;
  day_to: Date;
  from_time: string;
  to_time: string;
}

interface GroupProps {
  loading: boolean;
  error: boolean;
  bookingPlan: any;
  filter: GroupFilter;
}

interface DrawerState {
  open: boolean;
  entity?: string;
  element?: any;
}

const TODAY = new Date();
const DEFAULT_DAY_FROM = getStartOfWeek(TODAY);
const DEFAULT_DAY_TO = getEndOfWeek(TODAY);
const DEFAULT_FROM_TIME = '16:00';
const DEFAULT_TO_TIME = '22:00';
const DEFAULT_PUBLISHED = true;
const DEFAULT_STATUS = 'accepted';

function getTimetableDates(dt: Date) {
  const startOfWeek = getStartOfWeek(dt);

  return [
    startOfWeek,
    addDays(startOfWeek, 1),
    addDays(startOfWeek, 2),
    addDays(startOfWeek, 3),
    addDays(startOfWeek, 4),
    addDays(startOfWeek, 5),
    addDays(startOfWeek, 6)
  ];
}

function getTimetableGroup(group: GroupInterface, bookingPlan: any) {
  const domainFilter = bookingPlan.domains?.filter(
    d => d.id === group.domain_id
  );

  if (domainFilter.length > 0) {
    const groupFilter = domainFilter[0]?.groups.filter(g => g.id);

    if (groupFilter.length > 0) {
      return groupFilter[0];
    }
  }

  return null;
}

const useStyles = makeStyles((theme: DefaultTheme) => ({
  unstyledList: {
    margin: 0,
    listStyle: 'none',
    paddingLeft: 0,
    '& li': {
      marginBottom: theme.spacing(1)
    }
  },
  bulletList: {
    margin: 0,
    paddingLeft: theme.spacing(2),
    '& li': {
      marginBottom: theme.spacing(0.5)
    }
  },
  timeTableUnit: {},
  timeTableClosure: {
    background: 'rgb(204, 204, 204, .5)',
    opacity: 1,
    borderRadius: 0
  },
  timeTableClosureConflict: {
    background: 'rgb(204, 204, 204, .5)'
  },
  timeTableDate: {
    background: 'rgb(118, 148, 206, .5)',
    opacity: 1,
    borderRadius: 0
  },
  timeTableDateConflict: {
    background: 'rgb(118, 148, 206, .5)',
    opacity: 1
  },
  timeTableAllDay: {
    border: `2px solid ${theme.color.primary.main}`,
    borderRadius: 0,
    background: 'transparent',
    color: theme.color.text.main,
    fontWeight: 'bold',
    padding: '.25rem .5rem'
  },
  timeTableElementLabel: {
    fontWeight: 'bold',
    color: 'black',
    padding: '.1rem .5rem',
    fontSize: '.875rem'
  },
  barrierFreeBtn: {
    position: 'absolute',
    width: 0,
    height: 0,
    overflow: 'hidden',
    zIndex: -1,
    background: theme.color.background.default,
    '&:focus-visible': {
      width: 'auto',
      height: 'auto',
      zIndex: 1
    },
    '&:hover': {
      background: theme.color.background.default
    }
  },
  groupsList: {
    margin: 0,
    marginTop: theme.spacing(0.5),
    listStyle: 'none',
    paddingLeft: 0,
    '& > li': {
      marginBottom: theme.spacing(0.5)
    }
  }
}));

function Group({ initialProps }: { initialProps: GroupProps }) {
  const classes = useStyles();
  const intl = useIntl();
  const router = useRouter();
  const dispatch: AppDispatch = useDispatch();
  const { loading, filter, handleFilterChange } = useFilter<GroupFilter>(
    initialProps.filter
  );
  const [drawerState, setDrawerState] = useState<DrawerState>({ open: false });
  const [clusterDrawerOpen, setClusterDrawerOpen] = useState<boolean>(false);
  const [barrierFree, setBarrierFree] = useState<boolean>(false);
  const selectedBooking = useSelector(
    selectBookingById(drawerState.element?.booking_id)
  );
  const selectedDateSeries = useSelector(
    selectDateSeriesById(drawerState.element?.date_series_id)
  );
  const selectedDateSeriesLimits = useMemo(() => {
    if (selectedDateSeries) {
      return getDateSeriesLimits(selectedDateSeries);
    } else {
      return {
        start: null,
        end: null
      };
    }
  }, [selectedDateSeries]);
  const organization = useSelector(selectDefaultOrganization);
  const group = useSelector(selectGroupById(router.query.groupId as string));
  const provider = useSelector(selectProviderById(group?.provider_id));
  const [clientSide, setClientSide] = useState(false);

  useEffect(() => {
    // Make sure TimeTable is only available on client
    setTimeout(() => setClientSide(true), 50);
  }, []);

  if (initialProps.error) {
    return <PageNotFound />;
  }

  function handleTimeTableFilterChange(values: TimeTableFilterFormValues) {
    const fromHour = formatTime(values.timespan?.start);
    const toHour = formatTime(values.timespan?.end);

    if (fromHour && toHour) {
      handleFilterChange({
        day_from: getQueryString(getStartOfWeek(values.week)),
        day_to: getQueryString(getEndOfWeek(values.week)),
        from_time: fromHour?.string,
        to_time: toHour?.string
      });
    }
  }

  async function handleElementClick(element, data) {
    dispatch(fetchPublicBooking(data.element.booking_id));

    setDrawerState({
      open: true,
      element: data.element,
      entity: data.entity
    });
  }

  const timetableDates = getTimetableDates(filter.day_from ?? DEFAULT_DAY_FROM);
  const timetableGroup = getTimetableGroup(group, initialProps.bookingPlan);

  const groupHasLatLng =
    group.defaultAddress?.address_detail?.latitude &&
    group.defaultAddress?.address_detail?.longitude;
  const domainHasLatLng =
    group.domain?.defaultAddress?.address_detail?.latitude &&
    group.domain?.defaultAddress?.address_detail?.longitude;

  const unbookable = group.cluster
    ? group.cluster.unbookable
    : group.unbookable;
  const unbookableReason = group.cluster
    ? group.cluster.unbookable_reason
    : group.unbookable_reason;

  const externalBookingPlan = group.cluster
    ? group.cluster.external_booking_plan
    : group.external_booking_plan;
  const externalBookingPlanLink = group.cluster
    ? group.cluster.external_booking_plan_link
    : group.external_booking_plan_link;

  const linkListItems: {
    disabled?: boolean;
    title: any;
    subtitle?: string;
    href: string;
    icon?: string;
    hrefTitle?: string;
    external?: boolean;
  }[] = [
    {
      disabled: !groupHasLatLng && !domainHasLatLng,
      title: intl.formatMessage({
        id: 'show_map',
        defaultMessage: 'Show on map'
      }),
      href: groupHasLatLng
        ? `/map?domainId=${group.domain_id}&groupId=${group.id}`
        : `/map?domainId=${group.domain_id}`,
      hrefTitle: !(
        group.defaultAddress?.address_detail?.latitude &&
        group.defaultAddress?.address_detail?.longitude
      )
        ? intl.formatMessage({
            id: 'group_no_lat_long',
            defaultMessage: 'The group has no coordinates given'
          })
        : null
    }
  ];

  if (
    organization.setting_portal_booking_requests &&
    provider.setting_portal_booking_requests &&
    !unbookable
  ) {
    linkListItems.push({
      title: intl.formatMessage({
        id: 'request_times',
        defaultMessage: 'Request times'
      }),
      href: `/requests/create?providerId=${group.provider_id}&domainId=${group.domain_id}&groupId=${group.id}`
    });
  }

  if (provider.website_sports_facility_transfer_application && !unbookable) {
    linkListItems.push({
      title: intl.formatMessage({
        id: 'website_sports_facility_transfer_application',
        defaultMessage:
          'Application for the use of a sports facility (offline form)'
      }),
      href: provider.website_sports_facility_transfer_application,
      external: true
    });
  }

  if (provider.website_data_protection) {
    linkListItems.push({
      title: intl.formatMessage({
        id: 'data_protection_request',
        defaultMessage: 'Data protection'
      }),
      subtitle: ' (PDF Datei)',
      icon: 'download',
      href: provider.website_data_protection,
      external: true
    });
  }

  if (organization.website_span) {
    linkListItems.push({
      title: intl.formatMessage({
        id: 'terms_of_use',
        defaultMessage: 'Terms of use'
      }),
      icon: 'download',
      subtitle: ' (PDF Datei)',
      href: organization.website_span,
      external: true
    });
  }

  return (
    <>
      <Head>
        <title>{`${Globals.settings.titlePrefix}${group.name}${Globals.settings.titlePostfix(organization)}`}</title>
      </Head>
      <Breadcrumb>
        <Text>
          <Link href="/">
            {intl.formatMessage({
              id: 'sports_portal',
              defaultMessage: 'Sports portal Berlin'
            })}
          </Link>
        </Text>
        <Text>
          <Link href="/groups?unbookable=false">
            {intl.formatMessage({
              id: 'groups_find',
              defaultMessage: 'Find groups'
            })}
          </Link>
        </Text>
        <Text>{`${group.name} (${group.domain?.name} / ${group.domain.defaultAddress.address_detail.district})`}</Text>
      </Breadcrumb>
      <AuthenticationArea />
      <Spacing y={5} />
      <Heading type="h1">
        {group.cluster ? `${group.cluster.name} - ` : ''}
        {group.name}
      </Heading>
      {unbookable ||
      group.primary_use_by_state_or_federal_bases ||
      group.disruption ? (
        <>
          <Heading type="h2" icon="info-circle" iconLib="fa">
            {(() => {
              if (
                unbookable &&
                !group.primary_use_by_state_or_federal_bases &&
                !group.disruption
              ) {
                return intl.formatMessage({
                  id: 'warning_group_not_bookable',
                  defaultMessage: 'Group not bookable'
                });
              } else if (
                !unbookable &&
                group.primary_use_by_state_or_federal_bases &&
                !group.disruption
              ) {
                return intl.formatMessage({
                  id: 'primary_use_by_state_or_federal_bases',
                  defaultMessage: 'Primary use by state or federal bases'
                });
              } else if (
                !unbookable &&
                !group.primary_use_by_state_or_federal_bases &&
                group.disruption
              ) {
                return intl.formatMessage({
                  id: 'current_disruptions',
                  defaultMessage: 'Current disruptions'
                });
              } else {
                return intl.formatMessage({
                  id: 'current_hints_group',
                  defaultMessage: 'Current information about this group'
                });
              }
            })()}
          </Heading>
          {unbookable && !unbookableReason ? (
            <Text paragraph>
              {intl.formatMessage({
                id: 'warning_group_not_bookable_description',
                defaultMessage: 'Group not bookable'
              })}
            </Text>
          ) : null}
          {unbookable && unbookableReason ? (
            <Text paragraph>{unbookableReason}</Text>
          ) : null}
          {group.primary_use_by_state_or_federal_bases ? (
            <Text paragraph>
              {intl.formatMessage({
                id: 'warning_group_primary_use_by_state_or_federal_bases',
                defaultMessage: 'Group primary used by state or federal bases'
              })}
            </Text>
          ) : null}
          {group.disruption ? <Text paragraph>{group.disruption}</Text> : null}
          <Spacing y={3} />
        </>
      ) : null}

      <InformationGrid
        labelWidth={'15em'}
        title={
          <Heading type="h2">
            {intl.formatMessage({
              id: 'provider',
              defaultMessage: 'Provider'
            })}
          </Heading>
        }
      >
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'name',
            defaultMessage: 'Name'
          })}
        >
          {provider.name}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'address',
            defaultMessage: 'Address'
          })}
        >
          {formatDefaultAddressDetail(
            null,
            provider.defaultAddress?.address_detail
          )}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'website',
            defaultMessage: 'Website'
          })}
        >
          {provider.website ? (
            <a
              href={formatUrl(provider.website)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              {intl.formatMessage({
                id: 'go_to_website',
                defaultMessage: 'Go to website'
              })}
            </a>
          ) : null}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'email',
            defaultMessage: 'Email'
          })}
        >
          {provider.email}
        </InformationGrid.Item>
      </InformationGrid>

      <InformationGrid
        labelWidth={'15em'}
        title={
          <Heading type="h2">
            {intl.formatMessage({
              id: 'domain',
              defaultMessage: 'Domain'
            })}
          </Heading>
        }
      >
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'id',
            defaultMessage: 'Id'
          })}
        >
          {group.domain?.ufid}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'name',
            defaultMessage: 'Name'
          })}
        >
          {group.domain?.name}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'address',
            defaultMessage: 'Address'
          })}
        >
          {formatDefaultAddressDetail(
            null,
            getDefaultAddress(group.domain.addresses)?.address_detail
          )}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'webite',
            defaultMessage: 'Website'
          })}
        >
          {group?.domain?.website ? (
            <a
              href={formatUrl(group?.domain?.website)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              {intl.formatMessage({
                id: 'go_to_website',
                defaultMessage: 'Go to website'
              })}
            </a>
          ) : null}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'website_site_plan',
            defaultMessage: 'Site plan'
          })}
        >
          {group?.domain?.website_site_plan ? (
            <a
              href={formatUrl(group?.domain?.website_site_plan)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              {intl.formatMessage({
                id: 'go_to_website',
                defaultMessage: 'Go to website'
              })}
            </a>
          ) : null}
        </InformationGrid.Item>
      </InformationGrid>

      <InformationGrid
        labelWidth={'15em'}
        title={
          <Heading type="h2">
            {`${intl.formatMessage({
              id: 'geoinformation',
              defaultMessage: 'Geoinformation'
            })} (${intl.formatMessage({
              id: 'group',
              defaultMessage: 'Group'
            })})`}
          </Heading>
        }
      >
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'location_designation',
            defaultMessage: 'Location designation'
          })}
        >
          {group.defaultAddress?.address_detail?.location_description}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'separate_access_address',
            defaultMessage: 'Separate access address'
          })}
        >
          {group?.separate_access_address
            ? formatDefaultAddressDetail(
                null,
                group.defaultAddress?.address_detail
              ) || null
            : null}
        </InformationGrid.Item>
      </InformationGrid>

      <InformationGrid
        labelWidth={'15em'}
        title={
          <Heading type="h2">
            {intl.formatMessage({
              id: 'general_information',
              defaultMessage: 'General information'
            })}
          </Heading>
        }
      >
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'id',
            defaultMessage: 'Id'
          })}
        >
          {group.ufid}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'group_name',
            defaultMessage: 'Groupname'
          })}
        >
          {group.name}
        </InformationGrid.Item>
        {group.cluster ? (
          <InformationGrid.Item
            value={intl.formatMessage({
              id: 'cluster',
              defaultMessage: 'Cluster'
            })}
          >
            <a href="#" onClick={() => setClusterDrawerOpen(true)}>
              {group.cluster.name}
            </a>
          </InformationGrid.Item>
        ) : null}
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'category',
            defaultMessage: 'Category'
          })}
        >
          {group.type
            ? group.type_covered === 'covered'
              ? intl.formatMessage(jsonStrings['indoor'])
              : intl.formatMessage(jsonStrings['outdoor'])
            : null}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'group_type',
            defaultMessage: 'Group type'
          })}
        >
          {group.type && jsonStrings[`group_type_${group.type}`]
            ? intl.formatMessage(jsonStrings[`group_type_${group.type}`])
            : null}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'group_type_notes',
            defaultMessage: 'Group type notes'
          })}
        >
          {group.type_notes}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'flooring',
            defaultMessage: 'Flooring'
          })}
        >
          {group.flooring && jsonStrings[`group_flooring_${group.flooring}`]
            ? intl.formatMessage(
                jsonStrings[`group_flooring_${group.flooring}`]
              )
            : null}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'group_flooring_notes',
            defaultMessage: 'Group flooring notes'
          })}
        >
          {group.flooring_notes}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'description',
            defaultMessage: 'Description'
          })}
        >
          {group.description}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'sports_area_length_in_m',
            defaultMessage: 'Sports area length (in m)'
          })}
        >
          {group.sports_area_length_in_meters ? (
            <FormatNumber>{group.sports_area_length_in_meters}</FormatNumber>
          ) : null}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'sports_area_width_in_m',
            defaultMessage: 'Sports area width (in m)'
          })}
        >
          {group.sports_area_width_in_meters ? (
            <FormatNumber>{group.sports_area_width_in_meters}</FormatNumber>
          ) : null}
        </InformationGrid.Item>
        {group.type_covered === 'covered' ? (
          <InformationGrid.Item
            value={intl.formatMessage({
              id: 'height',
              defaultMessage: 'Height'
            })}
          >
            {group.height_in_meters ? (
              <FormatNumber>{group.height_in_meters}</FormatNumber>
            ) : null}
          </InformationGrid.Item>
        ) : null}
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'gross_sports_area_in_sqm',
            defaultMessage: 'Gross sports area (in sqm)'
          })}
        >
          {group.gross_sports_area_in_qm ? (
            <FormatNumber>{group.gross_sports_area_in_qm}</FormatNumber>
          ) : null}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'net_sports_area_in_sqm',
            defaultMessage: 'Net sports area (in sqm)'
          })}
        >
          {group.net_sports_area_in_qm ? (
            <FormatNumber>{group.net_sports_area_in_qm}</FormatNumber>
          ) : null}
        </InformationGrid.Item>
        <InformationGrid.Item
          value={intl.formatMessage({
            id: 'group_units',
            defaultMessage: 'Allocation'
          })}
        >
          {group.units?.length && group.units?.length > 1 ? (
            <ul className={classes.bulletList}>
              {group.units.map(u => (
                <li key={u.id}>{u.name}</li>
              ))}
            </ul>
          ) : null}
        </InformationGrid.Item>
      </InformationGrid>

      <LinkList items={linkListItems} />
      <Spacing y={3} />
      {provider.setting_portal_booking_plans_hidden === true ? null : (
        <>
          <Heading type="h2">
            {intl.formatMessage({
              id: 'calendar',
              defaultMessage: 'Calendar'
            })}
          </Heading>
          {unbookable || (externalBookingPlan && externalBookingPlanLink) ? (
            <Text>
              {externalBookingPlan && externalBookingPlanLink ? (
                <a
                  href={externalBookingPlanLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  {intl.formatMessage({
                    id: 'show_external_booking_plan',
                    defaultMessage: 'Show External booking plan'
                  })}
                </a>
              ) : null}
              {!externalBookingPlan && unbookable
                ? intl.formatMessage({
                    id: 'warning_group_not_bookable_booking_plan',
                    defaultMessage: 'Group not bookable'
                  })
                : null}
            </Text>
          ) : (
            <>
              <TimeTableFilterForm
                handleSubmit={values => handleTimeTableFilterChange(values)}
                initialValues={{
                  week: filter.day_to ?? DEFAULT_DAY_TO,
                  timespan: {
                    start: formatTime(filter.from_time || DEFAULT_FROM_TIME)
                      ?.string,
                    end: formatTime(filter.to_time || DEFAULT_TO_TIME)?.string
                  }
                }}
              />
              {clientSide ? (
                <>
                  <Spacing y={2} />
                  {/* @ts-ignore: no-typescript-component */}
                  <Button
                    className={classes.barrierFreeBtn}
                    onClick={() => setBarrierFree(true)}
                  >
                    {intl.formatMessage({
                      id: 'barrier_free_timetable',
                      defaultMessage: 'Show timetable barrier-free as Tabelle '
                    })}
                  </Button>
                  {barrierFree ? (
                    <>
                      <DateTable
                        // @ts-ignore: no-typescript-component
                        id="timetable-wrapper"
                        loading={loading}
                        handleElementClick={date =>
                          handleElementClick(null, {
                            element: date,
                            entity:
                              date.type === 'closure' ? 'closures' : 'dates'
                          })
                        }
                        holidays={initialProps.bookingPlan?.holidays || []}
                        publicHolidays={
                          initialProps.bookingPlan?.public_holidays || []
                        }
                        dates={[]
                          .concat(initialProps.bookingPlan?.dates || [])
                          .concat(initialProps.bookingPlan?.closures || [])}
                      />
                    </>
                  ) : (
                    <TimeTable.Week
                      loading={loading}
                      days={timetableDates}
                      data={{
                        dates: initialProps.bookingPlan?.dates || [],
                        closures: initialProps.bookingPlan?.closures || [],
                        holidays: initialProps.bookingPlan?.holidays || [],
                        public_holidays:
                          initialProps.bookingPlan?.public_holidays || []
                      }}
                      permissions={{
                        slotClick: () => false,
                        elementClick: () => true,
                        dragAndDrop: () => false,
                        resizeDateEnd: () => false,
                        resizeUnits: () => false
                      }}
                      group={timetableGroup}
                      onElementClick={(element, data) =>
                        handleElementClick(element, data)
                      }
                      customize={{
                        element: {
                          className: (event, entity) => {
                            const hasConflict =
                              event.conflicts?.length ||
                              event.closure_conflicts?.length;

                            switch (entity) {
                              case 'holidays':
                              case 'public_holidays':
                                return classes.timeTableAllDay;
                              case 'closures':
                                return classNames([
                                  classes.timeTableClosure,
                                  hasConflict
                                    ? classes.timeTableClosureConflict
                                    : null
                                ]);
                              case 'dates':
                              default:
                                return classNames([
                                  classes.timeTableDate,
                                  hasConflict
                                    ? classes.timeTableDateConflict
                                    : null
                                ]);
                            }
                          },
                          label: (element, entity, style) => {
                            const detailedLabel = style.width > 80;

                            switch (entity) {
                              case 'closures':
                                return (
                                  <div
                                    className={classes.timeTableElementLabel}
                                  >
                                    {detailedLabel ? (
                                      <>
                                        {
                                          convertToTimeString(
                                            new Date(element.start)
                                          ).pretty
                                        }
                                        -
                                        {
                                          convertToTimeString(
                                            new Date(element.end)
                                          ).pretty
                                        }
                                      </>
                                    ) : null}
                                    <br />
                                    {shortenText(
                                      intl.formatMessage({
                                        id: 'blocked',
                                        defaultMessage: 'Blocked'
                                      }),
                                      detailedLabel ? 10 : 2
                                    ).toLowerCase()}
                                    {detailedLabel ? (
                                      <>
                                        <br />
                                        {jsonStrings[
                                          `closure_type_${element.closure_type}`
                                        ]
                                          ? intl.formatMessage(
                                              jsonStrings[
                                                `closure_type_${element.closure_type}`
                                              ]
                                            )
                                          : `closure_type_${element.closure_type}`}
                                      </>
                                    ) : null}
                                  </div>
                                );
                              default:
                              case 'dates':
                                return (
                                  <div
                                    className={classes.timeTableElementLabel}
                                  >
                                    {detailedLabel ? (
                                      <>
                                        {
                                          convertToTimeString(
                                            new Date(element.start)
                                          ).pretty
                                        }
                                        -
                                        {
                                          convertToTimeString(
                                            new Date(element.end)
                                          ).pretty
                                        }
                                      </>
                                    ) : null}
                                    <br />
                                    {shortenText(
                                      intl.formatMessage({
                                        id: 'occupied',
                                        defaultMessage: 'Occupied'
                                      }),
                                      detailedLabel ? 10 : 2
                                    ).toLowerCase()}
                                  </div>
                                );
                            }
                          }
                        },
                        unit: {
                          className: unit => classes.timeTableUnit
                        }
                      }}
                      showFrom={
                        filter.from_time ? filter.from_time : DEFAULT_FROM_TIME
                      }
                      showTill={
                        filter.to_time ? filter.to_time : DEFAULT_TO_TIME
                      }
                    />
                  )}
                </>
              ) : null}

              <Drawer
                open={drawerState.open}
                onClose={() => setDrawerState({ open: false })}
              >
                {drawerState.element && drawerState.entity ? (
                  <Box p={3} pt={2}>
                    <Heading type="h2">
                      {`${convertToString(new Date(drawerState.element.start), {
                        withDay: true,
                        withTime: false,
                        withYear: false
                      })} ${
                        convertToTimeString(new Date(drawerState.element.start))
                          .pretty
                      } - ${
                        convertToTimeString(new Date(drawerState.element.end))
                          .pretty
                      }`}
                    </Heading>
                    <Spacing y={2} />
                    {selectedBooking && selectedDateSeries ? (
                      <ul className={classes.unstyledList}>
                        <li>
                          {`${intl.formatMessage({
                            id: 'status',
                            defaultMessage: 'Status'
                          })}: ${
                            drawerState.entity === 'closures'
                              ? intl.formatMessage({
                                  id: 'blocked',
                                  defaultMessage: 'Blocked'
                                })
                              : intl.formatMessage({
                                  id: 'occupied',
                                  defaultMessage: 'Occupied'
                                })
                          }`}
                        </li>
                        {drawerState.element.season_id ? (
                          <li>
                            {`${intl.formatMessage({
                              id: 'category',
                              defaultMessage: 'Category'
                            })}: ${
                              drawerState.entity === 'closures'
                                ? intl.formatMessage({
                                    id: 'closure_permanent',
                                    defaultMessage: 'Permanent'
                                  })
                                : intl.formatMessage({
                                    id: 'occupancy_permanent',
                                    defaultMessage: 'Permanent'
                                  })
                            }`}
                          </li>
                        ) : drawerState.element.holiday_id ? (
                          <li>
                            {`${intl.formatMessage({
                              id: 'category',
                              defaultMessage: 'Category'
                            })}: ${intl.formatMessage({
                              id: 'occupancy_holiday',
                              defaultMessage: 'Holiday'
                            })}`}
                          </li>
                        ) : (
                          <li>
                            {`${intl.formatMessage({
                              id: 'category',
                              defaultMessage: 'Category'
                            })}: ${
                              drawerState.entity === 'closures'
                                ? intl.formatMessage({
                                    id: 'closure_single',
                                    defaultMessage: 'Single'
                                  })
                                : intl.formatMessage({
                                    id: 'occupancy_single',
                                    defaultMessage: 'Single'
                                  })
                            }`}
                          </li>
                        )}
                        {drawerState.entity === 'closures' ? (
                          <li>
                            {`${intl.formatMessage({
                              id: 'closure_occupancy',
                              defaultMessage: 'Closure occupancy'
                            })}: ${intl.formatMessage(
                              jsonStrings[
                                `closure_type_${drawerState.element.closure_type}`
                              ]
                            )}`}
                          </li>
                        ) : (
                          <li>
                            {`${intl.formatMessage({
                              id: 'occupancy_activity',
                              defaultMessage: 'Occupancy activity'
                            })}: ${intl.formatMessage(
                              jsonStrings[drawerState.element.type]
                            )}`}
                          </li>
                        )}
                        {drawerState.entity === 'dates' ? (
                          <li>
                            {`${intl.formatMessage({
                              id: 'consumer',
                              defaultMessage: 'Consumer'
                            })}: ${
                              drawerState.element.consumer_type ===
                                'association' ||
                              drawerState.element.consumer_type === 'company' ||
                              drawerState.element.consumer_type === 'school' ||
                              drawerState.element.consumer_type ===
                                'sports_club'
                                ? drawerState.element.consumer_name
                                : drawerState.element.consumer_type
                                  ? intl.formatMessage(
                                      jsonStrings[
                                        drawerState.element.consumer_type
                                      ]
                                    )
                                  : intl.formatMessage({
                                      id: 'private_person',
                                      defaultMessage: 'Private person'
                                    })
                            }`}
                          </li>
                        ) : null}
                        {drawerState.entity === 'dates' &&
                        selectedBooking.occasion ? (
                          <li>
                            {`${intl.formatMessage({
                              id: 'occasion',
                              defaultMessage: 'Occasion'
                            })}: ${
                              (selectedBooking.occasion as Occasion)?.name
                            }`}
                          </li>
                        ) : null}
                        {drawerState.element.season_id ||
                        drawerState.element.holiday_id ? (
                          <>
                            {drawerState.element.season_id &&
                            selectedBooking.season ? (
                              <li>
                                {`${intl.formatMessage({
                                  id: 'season',
                                  defaultMessage: 'Season'
                                })}: ${
                                  (selectedBooking.season as Season)?.name
                                }`}
                              </li>
                            ) : null}{' '}
                            {drawerState.element.holiday_id &&
                            selectedBooking.holiday ? (
                              <li>
                                {`${intl.formatMessage({
                                  id: 'holiday',
                                  defaultMessage: 'Holiday'
                                })}: ${
                                  (selectedBooking.holiday as Holiday)?.name
                                }`}
                              </li>
                            ) : null}
                            <li>
                              {`${intl.formatMessage({
                                id: 'period',
                                defaultMessage: 'Period'
                              })}: ${
                                jsonStrings[selectedDateSeries.repeat_cycle]
                                  ? intl.formatMessage(
                                      jsonStrings[
                                        selectedDateSeries.repeat_cycle
                                      ]
                                    )
                                  : ''
                              }${
                                !selectedDateSeries.repeat_cycle.startsWith(
                                  'daily'
                                )
                                  ? `, ${intl
                                      .formatMessage(
                                        jsonStrings[
                                          Weekdays[
                                            (new Date(
                                              selectedDateSeriesLimits.start
                                            ).getDay() +
                                              6) %
                                              7
                                          ]
                                        ]
                                      )
                                      .substring(0, 2)}`
                                  : ''
                              }, ${
                                convertToTimeString(
                                  new Date(selectedDateSeriesLimits.start)
                                ).pretty
                              } - ${
                                convertToTimeString(
                                  new Date(selectedDateSeriesLimits.end)
                                ).pretty
                              } ${intl.formatMessage({
                                id: 'time_indication',
                                defaultMessage: "o'clock"
                              })}`}
                            </li>
                            <li>
                              {`${intl.formatMessage({
                                id: 'start_date',
                                defaultMessage: 'Start date'
                              })}: ${convertToString(
                                new Date(selectedDateSeriesLimits.start),
                                {
                                  withDay: false,
                                  withTime: false,
                                  withYear: true
                                }
                              )}`}
                            </li>
                            <li>
                              {`${intl.formatMessage({
                                id: 'end_date',
                                defaultMessage: 'End date'
                              })}: ${convertToString(
                                new Date(selectedDateSeriesLimits.end),
                                {
                                  withDay: false,
                                  withTime: false,
                                  withYear: true
                                }
                              )}`}
                            </li>
                          </>
                        ) : (
                          <>
                            <li>
                              {`${intl.formatMessage({
                                id: 'period',
                                defaultMessage: 'Period'
                              })}: ${
                                convertToTimeString(
                                  new Date(selectedDateSeriesLimits.start)
                                ).pretty
                              } - ${
                                convertToTimeString(
                                  new Date(selectedDateSeriesLimits.end)
                                ).pretty
                              } ${intl.formatMessage({
                                id: 'time_indication',
                                defaultMessage: "o'clock"
                              })}`}
                            </li>
                            <li>
                              {`${intl.formatMessage({
                                id: 'date',
                                defaultMessage: 'Date'
                              })}: ${convertToString(
                                new Date(selectedDateSeriesLimits.start),
                                {
                                  withDay: false,
                                  withTime: false,
                                  withYear: true
                                }
                              )}`}
                            </li>
                          </>
                        )}
                        <li>
                          {`${intl.formatMessage({
                            id: 'dates_single',
                            defaultMessage: 'Date'
                          })}: ${drawerState.element.position}`}
                        </li>
                      </ul>
                    ) : (
                      <Box display="flex" justifyContent="center" mt={10}>
                        <LoadingIndicator loading />
                      </Box>
                    )}
                  </Box>
                ) : null}
              </Drawer>
            </>
          )}
          <Spacing y={6} />
        </>
      )}
      {group?.json_fields && (
        <>
          {/* @ts-ignore: no-typescript-component */}
          <JsonFields
            labelWidth={'15em'}
            categories={organization?.group_json_fields?.categories}
            fields={group.json_fields}
            disableCategoryIcons
            filter={field => {
              if (
                field.indoor_outdoor_all === 'all' ||
                (group.type_covered === 'covered' &&
                  field.indoor_outdoor_all === 'indoor') ||
                (group.type_covered === 'not_covered' &&
                  field.indoor_outdoor_all === 'outdoor')
              ) {
                return true;
              }

              return false;
            }}
          />
        </>
      )}
      {group.cluster ? (
        <ClusterDrawer
          cluster={group.cluster}
          open={clusterDrawerOpen}
          onClose={() => setClusterDrawerOpen(false)}
        />
      ) : null}
    </>
  );
}

Group.getInitialProps = wrapper.getInitialPageProps(
  store =>
    async ({ query }) => {
      const groupResult = await store.dispatch(
        fetchPortalGroupWithUnits(query.groupId as string)
      );
      const providerId =
        // @ts-ignore: TODO: https://redux-toolkit.js.org/usage/usage-with-typescript#defining-action-contents-with-prepare-callbacks
        groupResult?.payload?.groups[query.groupId]?.provider_id;

      if (providerId) await store.dispatch(fetchProvider(providerId));

      const bookingPlanResult = await store.dispatch(
        fetchBookingPlans({
          group_id: query.groupId as string,
          mode: 'week_view',
          day_from:
            (query.day_from as string) ?? getQueryString(DEFAULT_DAY_FROM),
          day_to: (query.day_to as string) ?? getQueryString(DEFAULT_DAY_TO),
          published: DEFAULT_PUBLISHED,
          status: DEFAULT_STATUS
        })
      );

      return {
        // @ts-ignore: TODO: https://redux-toolkit.js.org/usage/usage-with-typescript#defining-action-contents-with-prepare-callbacks
        error: !!groupResult.error,
        // @ts-ignore
        bookingPlan: bookingPlanResult?.payload
      };
    }
);

Group.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};

export default Group;
